/**
 * The CSV round-trip client rules (spec 05-TBL §2.1 / §5.2) — all pure, no server call:
 *
 *  - {@link serializeScheduleCsv} builds the export (`Papa.unparse`) mirroring the current
 *    view's columns/order, each date column followed by its source annotation, with the
 *    spreadsheet-formula-injection guard.
 *  - {@link parseScheduleCsv} reads ONLY the Slot ID + four date columns (every other
 *    column ignored), surfacing file-level problems as an inline error.
 *  - {@link validateScheduleCsv} produces the {@link CsvValidationReport} AND the write set
 *    an apply would perform. It DELEGATES the locked + lifecycle-order checks to the same
 *    rule module the chart's edit layer uses ({@link shiftKeepsOrder} / the `locked` flag) —
 *    one rule implementation, two callers (gestures + CSV) — layering the CSV-only rules
 *    (matching, duplicates, malformed / past dates, no-op equality) on top.
 *
 * Validation is pure: identical file against identical state gives identical output. The
 * calculate-time blocking validator (04-GNT) remains the trust boundary behind it.
 */

import Papa from 'papaparse';
import moment from 'moment';
import { MILESTONE_LABELS, MILESTONE_ORDER } from './constants';
import { overlayRowDates, parseIsoDay, shiftKeepsOrder } from './utils';
import {
  dateColumnLabel,
  SLOT_ID_LABEL,
  sourceAnnotation,
  sourceColumnLabel,
  TEXT_COLUMN_LABELS,
  type ScheduleTableRow,
  type ScheduleTextColumnId,
} from './table-model';
import type {
  CsvRowError,
  CsvValidationReport,
  DateOverrideWrite,
  MergedDate,
  MilestoneKey,
  ScheduleDialogPayload,
  ScheduleOverrideEntry,
} from './types';

// ---------------------------------------------------------------------------
// Export (spec 5.2)
// ---------------------------------------------------------------------------

/** One ordered export column: a pinned text column or a date column (+ its source annotation). */
export type CsvExportColumn =
  | { kind: 'text'; id: ScheduleTextColumnId }
  | { kind: 'date'; field: MilestoneKey };

/**
 * Guards a cell against spreadsheet-formula injection (spec 5.2): a value beginning with
 * `=`, `+`, `-`, or `@` is prefixed with a single quote so a spreadsheet treats it as text.
 *
 * @param {string} value - The raw cell value.
 * @return {string} The escaped value.
 */
export function escapeFormula(value: string): string {
  return /^[=+\-@]/.test(value) ? `'${value}` : value;
}

/**
 * Reverses {@link escapeFormula} on read: strips a leading `'` that guards a formula
 * character, so an exported value round-trips to its original (keeps the Slot ID match
 * key and any date cell identical to what was written, even for the rare id that leads
 * with `=`/`+`/`-`/`@`).
 *
 * @param {string} value - The raw cell value.
 * @return {string} The unescaped value.
 */
export function unescapeFormula(value: string): string {
  return /^'[=+\-@]/.test(value) ? value.slice(1) : value;
}

/**
 * Serializes the current view to a CSV string (spec 5.2): identical labels, count, and
 * order to what is on screen (the caller passes the visible columns in display order and
 * the already sorted + filtered rows), each date column immediately followed by a
 * source-annotation column that upload ignores. Every cell is formula-escaped.
 *
 * The export ALWAYS leads with a "Slot ID" column — the round-trip match key upload
 * requires — even though Slot ID is no longer a visible table column, so the visible
 * columns need not (and must not) include it.
 *
 * @param {ScheduleTableRow[]} rows - The rows to export, in display (sorted + filtered) order.
 * @param {CsvExportColumn[]} columns - The visible columns, in display order (Slot ID excluded).
 * @return {string} The CSV text.
 */
export function serializeScheduleCsv(
  rows: ScheduleTableRow[],
  columns: CsvExportColumn[]
): string {
  const fields: string[] = [SLOT_ID_LABEL];
  for (const column of columns) {
    if (column.kind === 'text') {
      fields.push(TEXT_COLUMN_LABELS[column.id]);
    } else {
      fields.push(dateColumnLabel(column.field));
      fields.push(sourceColumnLabel(column.field));
    }
  }

  const data = rows.map((row) => {
    const cells: string[] = [escapeFormula(row.slotId)];
    for (const column of columns) {
      if (column.kind === 'text') {
        cells.push(escapeFormula(String(row[column.id] ?? '')));
      } else {
        const merged = row.dates[column.field];
        cells.push(escapeFormula(merged.value ?? ''));
        cells.push(escapeFormula(sourceAnnotation(merged)));
      }
    }
    return cells;
  });

  return Papa.unparse({ fields, data });
}

// ---------------------------------------------------------------------------
// Parse (spec 2.1)
// ---------------------------------------------------------------------------

/** One parsed upload row: the Slot ID + only the PRESENT date cells (blank string = empty cell). */
export interface ParsedCsvRow {
  rowNumber: number;
  slotId: string;
  /** Present date columns only; an absent key = that column was missing for the row. */
  cells: Partial<Record<MilestoneKey, string>>;
}

export type CsvParseResult =
  { ok: true; rows: ParsedCsvRow[] } | { ok: false; fileError: string };

/**
 * Normalizes a header for matching (trim + lowercase).
 *
 * @param {string} header - The raw header cell.
 * @return {string} The normalized header.
 */
function normalizeHeader(header: string): string {
  return header.trim().toLowerCase();
}

/** Header → milestone map, built once from the milestone labels ("Permit" → permitDate). */
const MILESTONE_BY_HEADER: Record<string, MilestoneKey> = Object.fromEntries(
  MILESTONE_ORDER.map((field) => [
    normalizeHeader(MILESTONE_LABELS[field]),
    field,
  ])
) as Record<string, MilestoneKey>;

const SLOT_ID_HEADER = normalizeHeader(SLOT_ID_LABEL);

/**
 * Parses upload text, reading ONLY the Slot ID column and the four date columns (every
 * other column — source annotations, names, units — is ignored). File-level problems
 * (unreadable file, missing Slot ID column, no recognizable date columns) fail with an
 * inline error before any report. A headers-only file is valid: an empty row set.
 *
 * @param {string} text - The raw CSV text.
 * @return {CsvParseResult} The parsed rows, or a file-level error.
 */
export function parseScheduleCsv(text: string): CsvParseResult {
  // `skipEmptyLines: false` keeps every line in `data`, so `index + 2` stays the TRUE
  // file line number (a blank interior line no longer shifts later rows' reported
  // numbers). Fully-blank lines are dropped below, after the number is fixed.
  const parsed = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: false,
    dynamicTyping: false,
  });

  const fields = parsed.meta.fields ?? [];
  if (fields.length === 0) {
    return { ok: false, fileError: 'Could not read the file as a CSV.' };
  }

  const slotIdField = fields.find((f) => normalizeHeader(f) === SLOT_ID_HEADER);
  if (!slotIdField) {
    return {
      ok: false,
      fileError:
        'The file has no “Slot ID” column. Export the table to get the template.',
    };
  }

  const dateFieldByMilestone = new Map<MilestoneKey, string>();
  for (const field of fields) {
    const milestone = MILESTONE_BY_HEADER[normalizeHeader(field)];
    if (milestone && !dateFieldByMilestone.has(milestone)) {
      dateFieldByMilestone.set(milestone, field);
    }
  }
  if (dateFieldByMilestone.size === 0) {
    return {
      ok: false,
      fileError:
        'The file has no schedule date columns (Permit, Spud, Completion, First production).',
    };
  }

  const rows: ParsedCsvRow[] = [];
  (parsed.data ?? []).forEach((record, index) => {
    const cells: Partial<Record<MilestoneKey, string>> = {};
    for (const [milestone, field] of dateFieldByMilestone) {
      const raw = record[field];
      // `undefined` = the column was missing for this row (a truncated line) → absent, a
      // no-op. An explicit empty string (a cell between two commas) = a blank cell.
      if (raw !== undefined && raw !== null) {
        cells[milestone] = unescapeFormula(String(raw).trim());
      }
    }
    const slotId = unescapeFormula((record[slotIdField] ?? '').trim());

    // Drop a fully-blank line (no id, no cell values) — never a row, never an error.
    const hasCellValue = Object.values(cells).some((value) => value !== '');
    if (slotId === '' && !hasCellValue) {
      return;
    }

    rows.push({
      rowNumber: index + 2, // +1 for the header row, +1 for 1-based numbering
      slotId,
      cells,
    });
  });

  return { ok: true, rows };
}

// ---------------------------------------------------------------------------
// Validate (spec 2.1)
// ---------------------------------------------------------------------------

export interface ValidateCsvInput {
  parsedRows: ParsedCsvRow[];
  payload: ScheduleDialogPayload;
  workingEntries: ScheduleOverrideEntry[];
}

export interface ValidateCsvResult {
  report: CsvValidationReport;
  /** The writes an apply performs — applied ONLY when `report.rowErrors` is empty. */
  writes: DateOverrideWrite[];
}

/**
 * Accepted upload date formats. ISO is the export's own format; the US `M/D` forms tolerate
 * hand-edits AND Excel's round-trip — opening the exported CSV and re-saving rewrites the date
 * column to the locale short date, commonly a 2-digit year (`11/1/24`), so the `…/YY` variants
 * are accepted too (moment's pivot maps `00–68` → `2000s`). US-oriented like the display and the
 * `M/D/YYYY` form already here; `D/M` is deliberately not accepted (genuinely ambiguous).
 */
const ACCEPTED_DATE_FORMATS = [
  'YYYY-MM-DD',
  'M/D/YYYY',
  'MM/DD/YYYY',
  'M/D/YY',
  'MM/DD/YY',
];

/**
 * Strictly parses a date cell to an ISO day, or null when unreadable.
 *
 * @param {string} raw - The trimmed cell value.
 * @return {string | null} The ISO day, or null.
 */
function parseCsvDate(raw: string): string | null {
  const m = moment.utc(raw, ACCEPTED_DATE_FORMATS, true);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
}

/**
 * Whether two ISO days denote the same calendar day.
 *
 * @param {string | null} a - The first ISO day.
 * @param {string | null} b - The second ISO day.
 * @return {boolean} True when both parse to the same day.
 */
function sameDay(a: string | null, b: string | null): boolean {
  if (a === null || b === null) {
    return false;
  }
  return parseIsoDay(a) === parseIsoDay(b);
}

function rowError(
  rowNumber: number,
  slotId: string | null,
  kind: CsvRowError['kind'],
  message: string
): CsvRowError {
  return { rowNumber, slotId, kind, message };
}

/**
 * Validates parsed rows against the loaded payload + the current working log, producing
 * the {@link CsvValidationReport} and the write set (spec 2.1). Semantics:
 *
 *  - blank / garbage Slot ID → `unreadableSlotId`; the same id on two rows → `duplicateSlotId`.
 *  - a well-formed Slot ID naming no slot → an `unmatchedRows` WARNING (apply ignores it).
 *  - per matched cell: a value EQUAL to the current effective value is a no-op (never a
 *    change, never an error — even on a reported milestone, so an unchanged export
 *    re-uploads clean); a blank on an overridden milestone is a revert; a CHANGED cell
 *    faces reported → past → order (the last delegated to the shared rule module).
 *
 * @param {ValidateCsvInput} input - The parsed rows, the payload, and the working log.
 * @return {ValidateCsvResult} The report + the write set an apply would perform.
 */
export function validateScheduleCsv({
  parsedRows,
  payload,
  workingEntries,
}: ValidateCsvInput): ValidateCsvResult {
  const order = payload.constraints.order;
  const effectiveMs = parseIsoDay(payload.effectiveDate);
  const rowBySlot = new Map(payload.rows.map((row) => [row.slotId, row]));

  // Index the working log by slot ONCE so the per-row effective-date overlay is O(1) in
  // the log size (a large upload against a large log stays linear, not quadratic).
  const entriesBySlot = new Map<string, ScheduleOverrideEntry[]>();
  for (const entry of workingEntries) {
    const list = entriesBySlot.get(entry.slotId);
    if (list) {
      list.push(entry);
    } else {
      entriesBySlot.set(entry.slotId, [entry]);
    }
  }

  const idOccurrences = new Map<string, number>();
  for (const parsed of parsedRows) {
    if (parsed.slotId) {
      idOccurrences.set(
        parsed.slotId,
        (idOccurrences.get(parsed.slotId) ?? 0) + 1
      );
    }
  }

  const rowErrors: CsvRowError[] = [];
  const unmatchedRows: CsvValidationReport['unmatchedRows'] = [];
  const writes: DateOverrideWrite[] = [];
  let matchableRowCount = 0;
  let changedDateCount = 0;
  let blankCellRevertCount = 0;

  for (const parsed of parsedRows) {
    const { rowNumber, slotId } = parsed;

    if (!slotId) {
      rowErrors.push(
        rowError(
          rowNumber,
          null,
          'unreadableSlotId',
          'Row has a blank or unreadable Slot ID.'
        )
      );
      continue;
    }
    if ((idOccurrences.get(slotId) ?? 0) > 1) {
      rowErrors.push(
        rowError(
          rowNumber,
          slotId,
          'duplicateSlotId',
          `Slot ID “${slotId}” appears on more than one row — the winning row is ambiguous.`
        )
      );
      continue;
    }
    const row = rowBySlot.get(slotId);
    if (!row) {
      unmatchedRows.push({ rowNumber, slotId });
      continue;
    }

    matchableRowCount += 1;
    const current = overlayRowDates(row, entriesBySlot.get(slotId) ?? []);
    const merged: Record<MilestoneKey, MergedDate> = { ...current };
    const rowChanges: DateOverrideWrite[] = [];
    const rowReverts: DateOverrideWrite[] = [];
    const rowFieldErrors: CsvRowError[] = [];

    // Pass 1 — classify each PRESENT cell in lifecycle order, building the merged row.
    for (const field of order) {
      if (!(field in parsed.cells)) {
        continue; // column missing for this row — a no-op
      }
      const raw = parsed.cells[field] ?? '';
      const base = row.dates[field];
      const cur = current[field];

      if (raw === '') {
        // A blank cell reverts an overridden milestone to modeled; otherwise a no-op.
        if (cur.source === 'override') {
          rowReverts.push({ slotId, field, value: null });
          merged[field] = base;
        }
        continue;
      }

      const iso = parseCsvDate(raw);
      if (iso === null) {
        rowFieldErrors.push(
          rowError(
            rowNumber,
            slotId,
            'malformedDate',
            `${MILESTONE_LABELS[field]} is not a readable date (“${raw}”).`
          )
        );
        continue;
      }
      if (sameDay(iso, cur.value)) {
        continue; // no-op equality — even on a reported milestone
      }
      // A CHANGED cell.
      if (base.locked) {
        rowFieldErrors.push(
          rowError(
            rowNumber,
            slotId,
            'editsReportedDate',
            `${MILESTONE_LABELS[field]} is reported to the regulator and cannot be changed.`
          )
        );
        continue;
      }
      if (effectiveMs !== null && (parseIsoDay(iso) as number) < effectiveMs) {
        rowFieldErrors.push(
          rowError(
            rowNumber,
            slotId,
            'pastDate',
            `${MILESTONE_LABELS[field]} is before the run's effective date (${payload.effectiveDate}).`
          )
        );
        continue;
      }
      merged[field] = { value: iso, source: 'override', locked: false };
      rowChanges.push({ slotId, field, value: iso });
    }

    // Pass 2 — lifecycle-order check on the post-apply merged row (shared rule module).
    const validChanges: DateOverrideWrite[] = [];
    for (const change of rowChanges) {
      const ms = parseIsoDay(change.value) as number;
      if (shiftKeepsOrder(merged, change.field, ms, order)) {
        validChanges.push(change);
      } else {
        rowFieldErrors.push(
          rowError(
            rowNumber,
            slotId,
            'outOfOrder',
            `${
              MILESTONE_LABELS[change.field]
            } would break the permit → spud → completion → first production order.`
          )
        );
      }
    }

    rowErrors.push(...rowFieldErrors);
    for (const revertWrite of rowReverts) {
      writes.push(revertWrite);
      blankCellRevertCount += 1;
    }
    for (const change of validChanges) {
      writes.push(change);
      changedDateCount += 1;
    }
  }

  return {
    report: {
      matchableRowCount,
      changedDateCount,
      rowErrors,
      unmatchedRows,
      blankCellRevertCount,
    },
    writes,
  };
}
